/* eslint-disable react/require-default-props */
import React from 'react';
import {FlatList, View} from 'react-native';
import {includes} from 'lodash';
import {noop} from 'lodash';

import {Banner} from '/component/base/Banner/Banner';
import Icon from '/component/base/Icon';
import {LoadingBox} from '/component/base/LoadingBox';
import {Text} from '/component/base/Text/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import {DownlineProfile} from '/constant/TestIDs';
import {ALERTS_STATE_IS_LOADING} from '/enum/alertsState.enum';
import {
  EXPIRING_REWARDS,
  EXPIRING_WELLNESS_CREDITS,
  NO_ORDER,
  PREMIER_CANCELLATIONS,
  RECENTLY_INACTIVE,
} from '/enum/peopleToContact.enum';
import testProperties from '/util/testing/testProperties';

import {useAlerts} from './Alerts.hook';
import {styles} from './Alerts.style';
interface AlertsProps {
  alerts?: Record<string, any>;
  dismissAlert?: (name: string) => void;
  getAlerts?: () => void;
  hasAlerts?: boolean;
  orders?: any[];
  activatedOn?: string;
  alertsState?: Record<string, any>;
  featureFlag?: Record<string, any>;
}

export const Alerts: React.FC<AlertsProps> = ({
  alerts = {
    [NO_ORDER]: {},
    [PREMIER_CANCELLATIONS]: {},
    [RECENTLY_INACTIVE]: {},
    [EXPIRING_REWARDS]: {},
    [EXPIRING_WELLNESS_CREDITS]: {},
  },
  dismissAlert = noop,
  hasAlerts = false,
  orders = [],
  activatedOn = '',
  alertsState = {
    [NO_ORDER]: {
      [ALERTS_STATE_IS_LOADING]: false,
    },
    [RECENTLY_INACTIVE]: {
      [ALERTS_STATE_IS_LOADING]: false,
    },
    [PREMIER_CANCELLATIONS]: {
      [ALERTS_STATE_IS_LOADING]: false,
    },
    [EXPIRING_WELLNESS_CREDITS]: {
      [ALERTS_STATE_IS_LOADING]: false,
    },
    [EXPIRING_REWARDS]: {
      [ALERTS_STATE_IS_LOADING]: false,
    },
  },
  featureFlag = {},
}) => {
  const {alertsToShow, keyExtractor, dismissedAlerts, ts, tt} = useAlerts({
    alerts,
    dismissAlert,
    orders,
    activatedOn,
    alertsState,
    featureFlag,
  });
  const getItem = ({title, description, bannerClose}: any) => (
    <Banner style={{base: styles.base}} verticalBanner alertLevel="error">
      <View
        style={styles.titleContainer}
        {...testProperties(DownlineProfile.alertView)}>
        <Text variant="button" {...testProperties(DownlineProfile.alertTitle)}>
          {title}
        </Text>
        <TouchableOpacity
          style={styles.closeButton}
          onPress={bannerClose}
          {...testProperties(null, true)}>
          <Text
            variant="body"
            style={styles.closeButtonTitle}
            {...testProperties(DownlineProfile.alertDismissButton)}>
            {ts('dismiss', 'Dismiss')}
          </Text>
          <Icon name="close" style={styles.closeButtonIcon} />
        </TouchableOpacity>
      </View>
      <Text
        variant="body"
        {...testProperties(DownlineProfile.alertDescription)}>
        {description}
      </Text>
    </Banner>
  );

  const getUndoItem = ({title, bannerClose}: any) => (
    <Banner style={{base: styles.base}} verticalBanner alertLevel="info">
      <View style={styles.titleContainer}>
        {tt('maintenanceBannerDescription', () => (
          <Text
            variant="button"
            {...testProperties(DownlineProfile.alertDismissedMessage)}>
            “{title}” dismissed
          </Text>
        ))}
        <TouchableOpacity
          style={styles.closeButton}
          onPress={bannerClose}
          {...testProperties(null, true)}>
          <Text
            variant="body"
            style={styles.closeButtonTitle}
            {...testProperties(DownlineProfile.alertUndoDismissButton)}>
            {ts('undo', 'Undo')}
          </Text>
        </TouchableOpacity>
      </View>
      <Text
        variant="body"
        {...testProperties(DownlineProfile.alertRemovedMessage)}>
        {ts('description', 'Alert removed from People to Contact')}
      </Text>
    </Banner>
  );

  const renderItem = ({item: {title, description, bannerClose, name}}: any) => {
    if (!includes(dismissedAlerts.current, name)) {
      return getItem({title, description, bannerClose});
    }

    return getUndoItem({title, bannerClose});
  };

  if (!hasAlerts) {
    return (
      <Gutter>
        <LoadingBox style={styles.loadingBox} />
      </Gutter>
    );
  }
  return (
    <Gutter>
      <FlatList
        data={alertsToShow}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        scrollEnabled={false}
      />
    </Gutter>
  );
};
